import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Save, Trash2, AlertTriangle, ListChecks } from "lucide-react";
import { useHomeOSState } from "../state/HomeOSStateProvider";
import { TASK_CATEGORIES, TASK_STATUSES } from "../data/homeOsMockData";
import { suggestMembersForTask } from "../utils/choreIntelligence";

const PRIORITIES = ["low", "medium", "high", "urgent"];
const ENERGIES = ["low", "medium", "high"];

const BLANK = {
  title: "", description: "", category: "Chores", assignedTo: null,
  dueDate: "", dueTime: "", recurrence: "", status: "Upcoming",
  priority: "medium", energyRequired: "low", approvalRequired: false, notes: "",
};

export function TaskEditModal({ open, onClose, taskId, mode = "edit" }) {
  const { tasks, members, addTask, updateTask, removeTask } = useHomeOSState();
  const existing = taskId ? tasks.find(t => t.id === taskId) : null;
  const [form, setForm] = useState(BLANK);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setConfirmDelete(false);
    if (mode === "add" || !existing) {
      setForm(BLANK);
    } else {
      setForm({
        title: existing.title || "", description: existing.description || "",
        category: existing.category || "Chores", assignedTo: existing.assignedTo ?? null,
        dueDate: existing.dueDate || "", dueTime: existing.dueTime || "",
        recurrence: existing.recurrence || "", status: existing.status || "Upcoming",
        priority: existing.priority || "medium", energyRequired: existing.energyRequired || "low",
        approvalRequired: !!existing.approvalRequired, notes: existing.notes || "",
      });
    }
  }, [open, taskId, mode, existing]);

  if (!open) return null;

  const set = (k, v) => setForm(s => ({ ...s, [k]: v }));

  /* ARA's quiet suggestion — derived live, only while unassigned. */
  const suggestion = form.assignedTo === null && form.title.trim()
    ? suggestMembersForTask({ id: taskId, title: form.title, description: form.description, notes: form.notes }, members, tasks)
    : null;

  const save = () => {
    if (!form.title.trim()) { toast.error("Give the task a title."); return; }
    const payload = {
      ...form,
      dueDate: form.dueDate || null,
      dueTime: form.dueTime || null,
      recurrence: form.recurrence || null,
    };
    if (mode === "add" || !existing) {
      addTask(payload);
      toast.success("Task added.");
    } else {
      updateTask(existing.id, payload);
      toast.success("Task updated.");
    }
    onClose();
  };

  const del = () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    removeTask(existing.id);
    toast(`Removed "${existing.title}".`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="task-edit-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-t-[28px] sm:rounded-[28px] shadow-none font-home animate-fadeUp">
        <div className="sticky top-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm z-10 px-6 pt-6 pb-4 border-b border-gray-100 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-11 h-11 rounded-2xl bg-transparent dark:bg-[color:var(--ara-ink)] flex items-center justify-center flex-shrink-0">
                <ListChecks className="w-5 h-5 text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]" strokeWidth={1.5} />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-extrabold text-gray-900 dark:text-gray-50">
                  {mode === "add" || !existing ? "Add task" : "Edit task"}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">Local change — kept in your Home OS.</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-gray-100 dark:bg-slate-800 flex items-center justify-center text-gray-400 hover:text-gray-600" data-testid="task-edit-close">
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-3">
          <Field label="Title" value={form.title} onChange={(v) => set("title", v)} placeholder="e.g. Pack school lunches" testId="task-edit-title" autoFocus />
          <Field label="Description" value={form.description} onChange={(v) => set("description", v)} placeholder="Helpful context" testId="task-edit-description" />

          <Pills label="Category" value={form.category} options={TASK_CATEGORIES} onChange={(v) => set("category", v)} testId="task-edit-category" />
          <Pills label="Status" value={form.status} options={TASK_STATUSES} onChange={(v) => set("status", v)} testId="task-edit-status" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Pills label="Priority" value={form.priority} options={PRIORITIES} onChange={(v) => set("priority", v)} testId="task-edit-priority" formatOption={(v) => v.charAt(0).toUpperCase() + v.slice(1)} />
            <Pills label="Energy" value={form.energyRequired} options={ENERGIES} onChange={(v) => set("energyRequired", v)} testId="task-edit-energy" formatOption={(v) => v.charAt(0).toUpperCase() + v.slice(1)} />
          </div>

          <div>
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">Assigned to</span>
            <div className="mt-1 flex flex-wrap gap-1.5" data-testid="task-edit-assignee">
              <PillBtn active={form.assignedTo === null} onClick={() => set("assignedTo", null)} testId="task-edit-assignee-none">No-one</PillBtn>
              <PillBtn active={form.assignedTo === "all"} onClick={() => set("assignedTo", "all")} testId="task-edit-assignee-all">Everyone</PillBtn>
              <PillBtn active={form.assignedTo === "ara"} onClick={() => set("assignedTo", "ara")} testId="task-edit-assignee-ara">ARA</PillBtn>
              {members.map(m => (
                <PillBtn key={m.id} active={form.assignedTo === m.id} onClick={() => set("assignedTo", m.id)} testId={`task-edit-assignee-${m.id}`}>
                  {m.name || "(no name)"}
                </PillBtn>
              ))}
            </div>
            {suggestion && suggestion.line && (
              <div className="mt-2.5" data-testid="task-edit-ara-suggestion">
                <p
                  className="italic text-[13px] leading-[1.5]"
                  style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: "var(--ara-ink-muted)", fontWeight: 400 }}
                >
                  {suggestion.line}
                </p>
                {suggestion.suggestions.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                    {suggestion.suggestions.map(s => (
                      <button
                        key={s.memberId}
                        type="button"
                        onClick={() => set("assignedTo", s.memberId)}
                        className="text-[12.5px] min-h-[32px] transition-opacity hover:opacity-70"
                        style={{ fontFamily: "Inter, system-ui, sans-serif", color: "var(--ara-ink)", fontWeight: 450, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 }}
                        aria-label={`Assign this task to ${s.name}`}
                        data-testid={`task-edit-ara-assign-${s.memberId}`}
                      >
                        Assign to {s.name} →
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <DateField label="Due date" value={form.dueDate} onChange={(v) => set("dueDate", v)} testId="task-edit-due-date" />
            <TimeField label="Due time" value={form.dueTime} onChange={(v) => set("dueTime", v)} testId="task-edit-due-time" />
            <Field label="Recurrence" value={form.recurrence} onChange={(v) => set("recurrence", v)} placeholder="e.g. daily, weekly" testId="task-edit-recurrence" />
          </div>

          <ApprovalToggle checked={form.approvalRequired} onChange={(v) => set("approvalRequired", v)} />
          <Field label="Notes" value={form.notes} onChange={(v) => set("notes", v)} placeholder="Anything to remember" testId="task-edit-notes" />
        </div>

        <div className="sticky bottom-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm px-6 py-4 border-t border-gray-100 dark:border-slate-800 space-y-2">
          {confirmDelete && (
            <div className="flex items-start gap-2 rounded-2xl border border-[color:var(--ara-line-soft)] bg-transparent px-3.5 py-3" data-testid="task-edit-delete-confirm">
              <AlertTriangle className="w-4 h-4 text-[color:var(--ara-ink-muted)] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <p className="text-xs font-bold text-[color:var(--ara-ink-muted)]">Remove this task from Home OS?</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            {existing && mode !== "add" && (
              <button
                onClick={del}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-2xl text-xs font-bold border transition-colors ${
                  confirmDelete
                    ? "bg-[color:var(--ara-ink)] hover:opacity-80 text-white border-rose-600"
                    : "bg-white text-[color:var(--ara-ink-muted)] border-[color:var(--ara-line-soft)] hover:bg-transparent"
                }`}
                data-testid="task-edit-delete-btn"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                {confirmDelete ? "Yes, remove" : "Delete task"}
              </button>
            )}
            <button onClick={onClose} className="ml-auto px-4 py-2.5 rounded-2xl text-sm font-bold text-gray-500 hover:bg-gray-100" data-testid="task-edit-cancel">Cancel</button>
            <button onClick={save} className="inline-flex items-center gap-1.5 bg-[#1a4d3e] hover:bg-[#163f33] text-white font-bold text-sm px-5 py-2.5 rounded-2xl shadow-md" data-testid="task-edit-save">
              <Save className="w-4 h-4" strokeWidth={1.5} />
              {mode === "add" || !existing ? "Add task" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId, autoFocus }) {
  return (
    <label className="block">
      <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</span>
      <input type="text" autoFocus={autoFocus} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1 w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-violet-300"
        data-testid={testId} />
    </label>
  );
}
function DateField({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</span>
      <input type="date" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-violet-300"
        data-testid={testId} />
    </label>
  );
}
function TimeField({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</span>
      <input type="time" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-violet-300"
        data-testid={testId} />
    </label>
  );
}
function Pills({ label, value, options, onChange, testId, formatOption }) {
  return (
    <div data-testid={testId}>
      <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</span>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {options.map(opt => (
          <PillBtn key={opt} active={value === opt} onClick={() => onChange(opt)} testId={`${testId}-${opt}`}>
            {formatOption ? formatOption(opt) : opt}
          </PillBtn>
        ))}
      </div>
    </div>
  );
}
function PillBtn({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold border transition-colors ${
        active ? "bg-[#1a4d3e] text-white border-[#1a4d3e]" : "bg-white dark:bg-slate-800 text-gray-600 border-gray-200 dark:border-slate-700 hover:border-[color:var(--ara-line-soft)]"
      }`} data-testid={testId}>{children}</button>
  );
}
function ApprovalToggle({ checked, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} aria-pressed={checked}
      className={`w-full flex items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors ${
        checked ? "bg-transparent border-[color:var(--ara-line-soft)]" : "bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:border-gray-300"
      }`} data-testid="task-edit-approval">
      <span className={`w-4.5 h-4.5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${checked ? "bg-transparent border-amber-500" : "bg-white border-gray-300"}`}>
        {checked && <span className="w-2 h-2 rounded-sm bg-white" />}
      </span>
      <span className="text-sm font-bold text-gray-700 dark:text-gray-200">Needs approval before ARA acts</span>
    </button>
  );
}
